import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Search, Menu, X, ShoppingBag, User, ChevronDown, MapPin } from "lucide-react";
import { useStore } from "../context/Store";
import { api } from "../lib/api";
import { PATTERNS, t, LOCALES } from "../i18n";
import { localeFlag } from "../lib/countries";

const LOCALE_LABEL = { en: "EN", fr: "FR", es: "ES", it: "IT", nl: "NL", de: "DE", ar: "AR" };

// Keyword-rich anchor text (matches each target page's primary keyword) + a hidden
// crawler-only description on every link to strengthen internal-linking SEO.
const CITY_KW = { en: (n) => `Flower delivery in ${n}`, fr: (n) => `Livraison de fleurs à ${n}`, es: (n) => `Envío de flores a ${n}`, ar: (n) => `توصيل الورود في ${n}`, it: (n) => `Consegna fiori a ${n}`, nl: (n) => `Bloemen bezorgen in ${n}`, de: (n) => `Blumenlieferung in ${n}` };
const CITY_DESC = { en: n => `Flowers for ${n}, Morocco. Check bouquets, neighbourhoods and available delivery dates.`, fr: n => `Des fleurs pour ${n}, au Maroc. Consultez les bouquets, quartiers et dates disponibles.`, es: n => `Flores para ${n}, Marruecos. Consulte ramos, barrios y fechas disponibles.`, ar: n => `ورود إلى ${n} في المغرب. راجع الباقات والأحياء والمواعيد المتاحة.`, it: n => `Fiori per ${n}, Marocco. Scopri i bouquet, i quartieri e le date di consegna disponibili.`, nl: n => `Bloemen voor ${n}, Marokko. Bekijk boeketten, wijken en beschikbare bezorgdata.`, de: n => `Blumen für ${n}, Marokko. Entdecken Sie Sträuße, Stadtteile und verfügbare Lieferdaten.` };
const OCC_KW = { en: (n) => `${n} flowers`, fr: (n) => `Fleurs · ${n}`, es: (n) => `Flores · ${n}`, ar: (n) => `ورود · ${n}`, it: (n) => `Fiori · ${n}`, nl: (n) => `Bloemen · ${n}`, de: (n) => `Blumen · ${n}` };
const OCC_DESC = { en: n => `Explore ${n} bouquets for delivery in Morocco.`, fr: n => `Découvrez les bouquets pour ${n} à livrer au Maroc.`, es: n => `Descubra ramos para ${n} con entrega en Marruecos.`, ar: n => `اكتشف باقات ${n} للتوصيل في المغرب.`, it: n => `Esplora i bouquet di ${n} per la consegna in Marocco.`, nl: n => `Ontdek ${n} boeketten voor bezorging in Marokko.`, de: n => `Entdecken Sie ${n} Sträuße für die Lieferung in Marokko.` };
const VIEW_ALL = { en: "View all", fr: "Voir tout", es: "Ver todo", ar: "عرض الكل", it: "Vedi tutti", nl: "Alles bekijken", de: "Alle ansehen" };
// German keeps nouns capitalised; Italian/Dutch put the verb around the noun.
const VIEW_ALL_FMT = { it: (n) => `Vedi tutto: ${n.toLowerCase()}`, nl: (n) => `Alle ${n.toLowerCase()} bekijken`, de: (n) => `Alle ${n} ansehen` };
const viewAll = (locale, noun) => (VIEW_ALL_FMT[locale] ? VIEW_ALL_FMT[locale](noun) : `${VIEW_ALL[locale] || VIEW_ALL.en} ${noun.toLowerCase()}`);

const MegaItem = ({ label, testid, viewAllTo, viewAllLabel, locale, children }) => (
  <div className="group" data-testid={`${testid}-mega`}>
    <button data-testid={testid} className="flex items-center gap-1 hover:text-brass-light transition-colors duration-200 py-2">
      {label} <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
    </button>
    <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 transition-opacity duration-200 absolute top-full inset-x-0 mx-auto w-[640px] max-w-[calc(100vw-2rem)] pt-3 z-50">
      <div className="bg-plaster text-ink border border-brass-light shadow-2xl w-full p-6">
        <div className="grid grid-cols-2 gap-x-8 gap-y-1">{children}</div>
        <Link to={viewAllTo} className="mt-4 inline-flex items-center gap-1 text-sm text-brass hover:text-brass-dark font-medium border-t border-brass-light pt-3 w-full">
          {viewAllLabel} →
        </Link>
      </div>
    </div>
  </div>
);

export const Header = () => {
  const { locale = "en" } = useParams();
  const { boot, currency, setCurrency, gifts, account, activeCity, cityChoice } = useStore();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const pat = PATTERNS[locale] || PATTERNS.en;
  const cities = boot?.cities || [];
  const occasions = boot?.occasions || [];
  const [mExp, setMExp] = useState("");
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    api.get("/articles", { params: { locale } }).then((r) => setGuides(r.data || [])).catch(() => {});
  }, [locale]);
  // The chosen city leads: the same-day link, the availability bar and its cut-off all follow it.
  const deliveringToday = [...cities.filter((c) => c.delivers_today)]
    .sort((a, b) => (a.id === activeCity?.id ? -1 : 0) - (b.id === activeCity?.id ? -1 : 0));
  const cutoffCity = deliveringToday.find((c) => c.id === activeCity?.id) || deliveringToday[0];
  const chosen = (cityChoice?.key && cityChoice.key !== "_skipped"
    ? cities.find((c) => c.key === cityChoice.key || c.slug === cityChoice.slug)
    : null) || null;

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/${locale}/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="bg-plaster border-b border-brass-light sticky top-0 z-40">
      <div className="bg-ink text-plaster">
        <div className="max-w-content mx-auto px-4 sm:px-6 h-[68px] flex items-center gap-3 sm:gap-6 min-w-0">
          <Link to={`/${locale}`} data-testid="logo-link" aria-label="Fleurinelle" className="flex items-center shrink-0">
            <img
              src="/brand/fleurinelle-header-dark.png"
              alt="Fleurinelle"
              width="1200"
              height="360"
              className="h-8 sm:h-9 md:h-10 w-auto block select-none"
              draggable="false"
            />
          </Link>
          <nav className="hidden xl:flex items-center gap-4 text-sm ms-3">
            <MegaItem label={t(locale, "occasions")} testid="nav-occasions" locale={locale}
                      viewAllTo={`/${locale}/${pat.occasion}`} viewAllLabel={viewAll(locale, t(locale, "occasions"))}>
              {occasions.slice(0, 8).map((o) => (
                <Link key={o.id} to={`/${locale}/${pat.occasion}/${o.slug}`} data-testid={`mega-occasion-${o.key}`}
                      className="py-1.5 text-sm text-charcoal hover:text-brass transition-colors">
                  {(OCC_KW[locale] || OCC_KW.en)(o.name)}
                  <span className="sr-only">. {(OCC_DESC[locale] || OCC_DESC.en)(o.name)}</span>
                </Link>
              ))}
            </MegaItem>
            <MegaItem label={t(locale, "cities")} testid="nav-cities" locale={locale}
                      viewAllTo={`/${locale}/cities`} viewAllLabel={viewAll(locale, t(locale, "cities"))}>
              {cities.slice(0, 8).map((c) => (
                <Link key={c.id} to={`/${locale}/${pat.city}/${c.slug}`} data-testid={`mega-city-${c.key}`}
                      className="flex items-center gap-3 py-1.5 group/city">
                  <img src={c.hero_image_url} alt="" loading="lazy" className="w-10 h-10 object-cover rounded-sm shrink-0 border border-brass-light" />
                  <span className="text-sm text-charcoal group-hover/city:text-brass transition-colors">
                    {(CITY_KW[locale] || CITY_KW.en)(c.name)}
                    <span className="sr-only">. {(CITY_DESC[locale] || CITY_DESC.en)(c.name)}</span>
                  </span>
                </Link>
              ))}
            </MegaItem>
            <Link data-testid="nav-sameday" to={`/${locale}/${pat.city}/${activeCity?.slug || "casablanca"}`} className="hover:text-brass-light transition-colors duration-200 whitespace-nowrap">
              {t(locale, "sameDay")}
            </Link>
            <MegaItem label={t(locale, "guides")} testid="nav-guides" locale={locale}
                      viewAllTo={`/${locale}/guides`} viewAllLabel={viewAll(locale, t(locale, "guides"))}>
              {guides.slice(0, 6).map((g) => (
                <Link key={g.id} to={`/${locale}/${pat.guide}/${g.slug}`} data-testid={`mega-guide-${g.slug}`}
                      className="flex items-center gap-3 py-1.5 group/guide col-span-2">
                  <img src={g.hero_image_url} alt="" loading="lazy" className="w-10 h-10 object-cover rounded-sm shrink-0 border border-brass-light" />
                  <span className="text-sm text-charcoal group-hover/guide:text-brass transition-colors leading-snug">
                    {g.title}
                    <span className="sr-only">. {g.excerpt || ""}</span>
                  </span>
                </Link>
              ))}
            </MegaItem>
            <Link data-testid="nav-about" to={`/${locale}/about`} className="hover:text-brass-light transition-colors duration-200">
              {t(locale, "about")}
            </Link>
          </nav>
          <div className="ms-auto flex items-center gap-1.5 sm:gap-3 min-w-0">
            <button
              type="button"
              onClick={() => window.fleurinelleOpenCityPicker?.()}
              data-testid="header-city-pill"
              className="hidden md:inline-flex items-center gap-1.5 text-xs border border-brass-light hover:border-brass-light hover:bg-plaster/10 px-2.5 py-1.5 transition-colors shrink-0 whitespace-nowrap"
              aria-label={t(locale, "chooseCity") || "Choose city"}
            >
              <MapPin size={13} className="text-brass-light shrink-0" />
              <span className="hidden xl:inline">{chosen ? t(locale, "deliveringTo") : ""}</span>
              <span className="truncate max-w-[12ch]">{chosen ? chosen.name : (t(locale, "chooseCity") || "Choose city")}</span>
              {chosen ? <span className="hidden lg:inline text-brass-light">· {t(locale, "changeCity")}</span> : null}
              <ChevronDown size={12} className="shrink-0" />
            </button>
            <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2 border border-ink-soft px-3 py-1.5 min-w-0">
              <Search size={15} className="text-brass-light" />
              <input
                data-testid="header-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t(locale, "search")}
                className="bg-transparent text-sm outline-none w-20 2xl:w-28 min-w-0 placeholder:text-plaster/50"
              />
            </form>
            <select
              data-testid="locale-switcher"
              value={locale}
              onChange={async (e) => {
                const to = e.target.value;
                const rest = decodeURIComponent(window.location.pathname.split("/").slice(2).join("/"));
                if (!rest) return navigate(`/${to}`);
                try {
                  const { data } = await api.get("/translate-path", { params: { path: rest, from_locale: locale, to_locale: to } });
                  navigate(`/${to}/${data.path}${window.location.search}`);
                } catch {
                  navigate(`/${to}/${rest}`);
                }
                return undefined;
              }}
              className="bg-ink text-plaster text-sm border border-ink-soft px-1.5 sm:px-2 py-1.5 max-w-[4.5rem] sm:max-w-none"
            >
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {`${localeFlag[l]} ${LOCALE_LABEL[l]}`}
                </option>
              ))}
            </select>
            <select
              data-testid="currency-switcher"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="hidden sm:block bg-ink text-plaster text-sm border border-ink-soft px-2 py-1.5"
            >
              {(boot?.currencies || [{ code: "EUR" }]).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
            <Link data-testid="account-link" to={`/${locale}/account`} className="p-2 hidden sm:block" title={t(locale, "account")}>
              <User size={18} className={account ? "text-brass-light" : ""} />
            </Link>
            <Link data-testid="cart-link" to={`/${locale}/checkout`} className="relative p-2">
              <ShoppingBag size={18} />
              {gifts?.length ? <span className="absolute -top-0.5 end-0 bg-brass text-ink text-[10px] w-4 h-4 flex items-center justify-center">{gifts.length}</span> : null}
            </Link>
            <button data-testid="mobile-menu-toggle" className="xl:hidden p-2" onClick={() => setOpen(!open)} aria-label={t(locale, "menu")}>
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {deliveringToday.length > 0 ? (
        <div data-testid="availability-bar" className="bg-plaster-deep border-b border-brass-light">
          <div className="max-w-content mx-auto px-5 sm:px-6 py-2 text-xs text-muted sm:overflow-x-auto sm:whitespace-nowrap">
            <span className="text-ink">{t(locale, "deliveringToday")}</span>{" "}
            <span className="sm:hidden">{(cutoffCity || deliveringToday[0])?.name}</span>
            <span className="hidden sm:inline">{deliveringToday.map((c) => c.name).join(" · ")}</span>
            {" "}— {t(locale, "orderBefore")}{" "}
            {cutoffCity?.same_day_cutoff} {t(locale, "localTime")}
          </div>
        </div>
      ) : null}

      {open ? (
        <nav data-testid="mobile-menu" className="xl:hidden bg-plaster border-b border-brass-light px-6 py-4 flex flex-col gap-1 text-sm max-h-[70vh] overflow-y-auto">
          <form onSubmit={(e) => { submitSearch(e); setOpen(false); }} className="flex items-center gap-2 border border-brass-light px-3 py-2 mb-2">
            <Search size={16} className="shrink-0" />
            <input data-testid="mobile-search-input" aria-label={t(locale, "search")} placeholder={t(locale, "search")} className="min-w-0 w-full bg-transparent outline-none" value={q} onChange={e => setQ(e.target.value)} />
            <button type="submit" data-testid="mobile-search-submit" aria-label={t(locale, "search")}><Search size={16} /></button>
          </form>
          <button data-testid="m-occasions-toggle" onClick={() => setMExp(mExp === "occ" ? "" : "occ")}
                  className="flex items-center justify-between py-2 text-ink font-medium">
            {t(locale, "occasions")} <ChevronDown size={16} className={`transition-transform ${mExp === "occ" ? "rotate-180" : ""}`} />
          </button>
          {mExp === "occ" ? (
            <div className="ps-3 flex flex-col gap-1 pb-2">
              {occasions.slice(0, 8).map((o) => (
                <Link key={o.id} to={`/${locale}/${pat.occasion}/${o.slug}`} onClick={() => setOpen(false)}
                      data-testid={`m-occasion-${o.key}`} className="py-1.5 text-charcoal">
                  {(OCC_KW[locale] || OCC_KW.en)(o.name)}
                  <span className="sr-only">. {(OCC_DESC[locale] || OCC_DESC.en)(o.name)}</span>
                </Link>
              ))}
              <Link to={`/${locale}/${pat.occasion}`} onClick={() => setOpen(false)} className="py-1.5 text-brass font-medium">{VIEW_ALL[locale] || VIEW_ALL.en} →</Link>
            </div>
          ) : null}

          <button data-testid="m-cities-toggle" onClick={() => setMExp(mExp === "cit" ? "" : "cit")}
                  className="flex items-center justify-between py-2 text-ink font-medium">
            {t(locale, "cities")} <ChevronDown size={16} className={`transition-transform ${mExp === "cit" ? "rotate-180" : ""}`} />
          </button>
          {mExp === "cit" ? (
            <div className="ps-3 flex flex-col gap-1 pb-2">
              {cities.slice(0, 8).map((c) => (
                <Link key={c.id} to={`/${locale}/${pat.city}/${c.slug}`} onClick={() => setOpen(false)}
                      data-testid={`m-city-${c.key}`} className="flex items-center gap-3 py-1.5">
                  <img src={c.hero_image_url} alt="" loading="lazy" className="w-8 h-8 object-cover rounded-sm border border-brass-light" />
                  <span className="text-charcoal">{(CITY_KW[locale] || CITY_KW.en)(c.name)}
                    <span className="sr-only">. {(CITY_DESC[locale] || CITY_DESC.en)(c.name)}</span>
                  </span>
                </Link>
              ))}
              <Link to={`/${locale}/cities`} onClick={() => setOpen(false)} className="py-1.5 text-brass font-medium">{VIEW_ALL[locale] || VIEW_ALL.en} →</Link>
            </div>
          ) : null}

          <button data-testid="m-guides-toggle" onClick={() => setMExp(mExp === "gui" ? "" : "gui")}
                  className="flex items-center justify-between py-2 text-ink font-medium">
            {t(locale, "guides")} <ChevronDown size={16} className={`transition-transform ${mExp === "gui" ? "rotate-180" : ""}`} />
          </button>
          {mExp === "gui" ? (
            <div className="ps-3 flex flex-col gap-1 pb-2">
              {guides.slice(0, 6).map((g) => (
                <Link key={g.id} to={`/${locale}/${pat.guide}/${g.slug}`} onClick={() => setOpen(false)}
                      data-testid={`m-guide-${g.slug}`} className="flex items-center gap-3 py-1.5">
                  <img src={g.hero_image_url} alt="" loading="lazy" className="w-8 h-8 object-cover rounded-sm border border-brass-light" />
                  <span className="text-charcoal leading-snug">{g.title}
                    <span className="sr-only">. {g.excerpt || ""}</span>
                  </span>
                </Link>
              ))}
              <Link to={`/${locale}/guides`} onClick={() => setOpen(false)} className="py-1.5 text-brass font-medium">{VIEW_ALL[locale] || VIEW_ALL.en} →</Link>
            </div>
          ) : null}
          <Link to={`/${locale}/about`} onClick={() => setOpen(false)} className="py-2 text-ink">{t(locale, "about")}</Link>
          <Link to={`/${locale}/account`} onClick={() => setOpen(false)} className="py-2 text-ink">{t(locale, "account")}</Link>
          <Link to={`/${locale}/track`} onClick={() => setOpen(false)} className="py-2 text-ink">{t(locale, "track")}</Link>
          <label className="sm:hidden flex items-center justify-between py-2 text-ink">
            <span>{currency}</span>
            <select data-testid="m-currency-switcher" value={currency} onChange={(e) => setCurrency(e.target.value)}
                    className="bg-plaster text-ink text-sm border border-brass-light px-2 py-1">
              {(boot?.currencies || [{ code: "EUR" }]).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </label>
        </nav>
      ) : null}
    </header>
  );
};

export default Header;
